import React, { useState, useEffect, useCallback, useRef } from 'react';
import { dataEntryApi, dynamicApi, devFilesApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import {
  Save, X, Plus, Settings2, Search, RefreshCw,
  List, Table, LayoutList, ChevronDown, ChevronLeft, ChevronRight, ChevronUp,
  GripVertical, SplitSquareHorizontal, ListFilter, ChevronsLeft,
  Type, Hash, Calendar, CheckSquare, AlignLeft, AlignCenter, AlignRight, Lock, Link2,
  Database, FileDown, DatabaseZap
} from 'lucide-react';

/**
 * DataEntryComposerPage - Visual builder for Data Entry modules.
 * Mirrors the Standard View Composer (lvc-* CSS classes throughout).
 *
 * Module = top-level entity (Commissioning, QA/QC, Estimating, etc.)
 * Each module has one or more Views (List View or Datasheet View, each tied to a DB table).
 * List Views get the full SVC form builder (24-col grid, sections, derived fields, related tabs).
 * Datasheet Views get the column editor (label/width/align/entryMode).
 * Both view types support entryMode per field (editable / display-only / hidden).
 */

// ─── Module-level utilities ────────────────────────────────────────────────────

const FIELD_TYPE_ICONS = {
  text: Type, number: Hash, date: Calendar, checkbox: CheckSquare,
  readonly: Lock, dropdown: List, searchselect: List, url: Link2,
  textarea: AlignLeft, default: AlignCenter
};

const getFieldIcon = (uiType) => {
  const Icon = FIELD_TYPE_ICONS[uiType?.toLowerCase()] || FIELD_TYPE_ICONS.default;
  return <Icon size={13} />;
};

const slugify = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const formatLabel = (fieldName) =>
  fieldName.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\b\w/g, c => c.toUpperCase());

const suggestFieldType = (dataType) => {
  const type = dataType?.toUpperCase() || '';
  if (type.includes('DATE')) return 'date';
  if (type === 'BIT') return 'checkbox';
  if (type.includes('INT') || type.includes('DECIMAL') || type.includes('NUMERIC') || type.includes('FLOAT')) return 'number';
  if (type.includes('TEXT') || (type.includes('CHAR') && type.includes('MAX'))) return 'textarea';
  return 'text';
};

const getTypeColor = (dataType) => {
  const type = dataType?.toUpperCase() || '';
  if (type.includes('CHAR') || type.includes('TEXT')) return 'type-string';
  if (type.includes('INT') || type.includes('DECIMAL') || type.includes('NUMERIC') || type.includes('FLOAT')) return 'type-number';
  if (type.includes('DATE') || type.includes('TIME')) return 'type-date';
  if (type === 'BIT') return 'type-boolean';
  return 'type-other';
};

const getShortType = (dataType) => {
  const type = dataType?.toUpperCase() || '';
  if (type.includes('NVARCHAR') || type.includes('VARCHAR')) return 'STR';
  if (type.includes('INT')) return 'INT';
  if (type.includes('DECIMAL') || type.includes('NUMERIC')) return 'NUM';
  if (type.includes('DATE')) return 'DATE';
  if (type === 'BIT') return 'BOOL';
  return type.split('(')[0]?.substring(0, 4) || '?';
};

const getFieldTypeIcon = (fieldType) => {
  switch (fieldType) {
    case 'checkbox': return <CheckSquare size={11} />;
    case 'dropdown': return <List size={11} />;
    case 'date': return <Calendar size={11} />;
    case 'number': return <Hash size={11} />;
    case 'textarea': return <AlignLeft size={11} />;
    case 'readonly': return <Lock size={11} />;
    case 'spacer': return <AlignCenter size={11} />;
    default: return <Type size={11} />;
  }
};

// ─── Component ─────────────────────────────────────────────────────────────────

const DataEntryComposerPage = () => {
  const { notify } = useNotifications();

  // ─── Module state ──────────────────────────────────────────────────────────
  const [modules, setModules] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState('');
  const [moduleConfig, setModuleConfig] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingModule, setLoadingModule] = useState(false);
  const [refreshingSchema, setRefreshingSchema] = useState(false);

  // ─── View state ────────────────────────────────────────────────────────────
  const [activeViewIndex, setActiveViewIndex] = useState(0);
  const [views, setViews] = useState([]);

  // ─── Field palette (shared by both view types) ─────────────────────────────
  const [paletteFields, setPaletteFields] = useState([]);
  const [paletteLoading, setPaletteLoading] = useState(false);
  const [paletteSearch, setPaletteSearch] = useState('');

  // ─── Canvas fields for active view ────────────────────────────────────────
  const [canvasFields, setCanvasFields] = useState([]);

  // ─── Table list (for Add View picker) ─────────────────────────────────────
  const [allTables, setAllTables] = useState([]);

  // ─── Datasheet drag-drop reorder ──────────────────────────────────────────
  const [draggedField, setDraggedField] = useState(null);
  const draggedFieldRef = useRef(null);

  // ─── Live preview (datasheet only) ────────────────────────────────────────
  const [previewData, setPreviewData] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // ─── Module/view modals ───────────────────────────────────────────────────
  const [showNewModuleModal, setShowNewModuleModal] = useState(false);
  const [newModuleName, setNewModuleName] = useState('');
  const [newModuleSlug, setNewModuleSlug] = useState('');
  const [newModuleIcon, setNewModuleIcon] = useState('');
  const [newModuleDesc, setNewModuleDesc] = useState('');

  const [showAddViewModal, setShowAddViewModal] = useState(false);
  const [addViewMode, setAddViewMode] = useState('new'); // 'new' | 'import'
  const [addViewType, setAddViewType] = useState('datasheet');
  const [addViewTable, setAddViewTable] = useState('');
  const [addViewLabel, setAddViewLabel] = useState('');
  const [importSourceTable, setImportSourceTable] = useState('');
  const [importSourceLabel, setImportSourceLabel] = useState('');
  const [importSvcConfigs, setImportSvcConfigs] = useState([]);
  const [importSvcLoading, setImportSvcLoading] = useState(false);
  const [importSourceConfig, setImportSourceConfig] = useState(null);
  const [importConfigLoading, setImportConfigLoading] = useState(false);

  const moduleDropdownRef = useRef(null);
  const [showModuleDropdown, setShowModuleDropdown] = useState(false);

  // ─── List View canvas state ────────────────────────────────────────────────
  const [lvListPanelCollapsed, setLvListPanelCollapsed] = useState(false);
  const [lvDraggedField, setLvDraggedField] = useState(null);
  const [lvDragOverRow, setLvDragOverRow] = useState(null);
  const [lvDragOverInsert, setLvDragOverInsert] = useState(null);
  const [lvEditingField, setLvEditingField] = useState(null);
  const [lvHeaderSettings, setLvHeaderSettings] = useState({ primaryLine: '', secondaryLine: '', sortField: '', sortDirection: 'ASC' });
  const [lvShowSectionModal, setLvShowSectionModal] = useState(false);
  const [lvNewSectionTitle, setLvNewSectionTitle] = useState('');
  const [lvSectionTargetRow, setLvSectionTargetRow] = useState(1);
  const [lvShowListConfigModal, setLvShowListConfigModal] = useState(false);
  const [lvShowDerivedModal, setLvShowDerivedModal] = useState(false);
  const [lvDerivedSourceField, setLvDerivedSourceField] = useState('');
  const [lvDerivedTargetField, setLvDerivedTargetField] = useState('');
  const [lvDerivedLabel, setLvDerivedLabel] = useState('');
  const [lvDerivedTargetFields, setLvDerivedTargetFields] = useState([]);
  const [lvDerivedTargetLoading, setLvDerivedTargetLoading] = useState(false);
  // ─── Load module list ──────────────────────────────────────────────────────
  const loadModules = useCallback(async () => {
    try {
      const res = await dataEntryApi.getModules();
      setModules(res.data);
    } catch {
      notify.error('Failed to load modules');
    }
  }, [notify]);

  const loadAllTables = useCallback(async () => {
    try {
      const res = await dataEntryApi.getProjectTables();
      setAllTables(res.data || []);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => {
    loadModules();
    loadAllTables();
  }, [loadModules, loadAllTables]);

  // Close module dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (moduleDropdownRef.current && !moduleDropdownRef.current.contains(e.target))
        setShowModuleDropdown(false);
    };
    if (showModuleDropdown) {
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }
  }, [showModuleDropdown]);

  // ─── Load module config when selected ─────────────────────────────────────
  const loadModule = useCallback(async (slug) => {
    if (!slug) return;
    try {
      setLoadingModule(true);
      const res = await dataEntryApi.getModule(slug);
      const data = res.data;
      setModuleConfig(data);
      const loadedViews = data.config?.views || [];
      setViews(loadedViews);
      setActiveViewIndex(0);
      const firstView = loadedViews[0];
      setCanvasFields(firstView?.fieldConfig || []);
      setLvHeaderSettings(firstView?.listPanelConfig || { primaryLine: '', secondaryLine: '', sortField: '', sortDirection: 'ASC' });
      setHasChanges(false);
    } catch {
      notify.error('Failed to load module');
    } finally {
      setLoadingModule(false);
    }
  }, [notify]);

  const handleSelectModule = (slug) => {
    if (hasChanges && !window.confirm('You have unsaved changes. Discard them?')) return;
    setSelectedSlug(slug);
    setShowModuleDropdown(false);
    loadModule(slug);
  };

  // ─── Toolbar actions ───────────────────────────────────────────────────────

  const activeView = views[activeViewIndex];

  const handleRefreshSchema = async () => {
    if (!activeView?.tableName) return;
    try {
      setRefreshingSchema(true);
      const res = await dataEntryApi.getTableSchema(activeView.tableName);
      setPaletteFields(res.data?.fields || []);
      notify.success('Schema refreshed');
    } catch {
      notify.error('Failed to refresh schema');
    } finally {
      setRefreshingSchema(false);
    }
  };

  const handleReloadConfig = () => {
    if (!selectedSlug) return;
    if (hasChanges && !window.confirm('Discard unsaved changes and reload?')) return;
    loadModule(selectedSlug);
  };

  const handleSyncFields = async () => {
    if (!activeView?.tableName) return;
    try {
      const res = await dataEntryApi.getTableSchema(activeView.tableName);
      const schemaFields = res.data?.fields || [];
      const schemaMap = {};
      schemaFields.forEach(f => { schemaMap[f.fieldName] = f; });

      setCanvasFields(prev => prev.map(f => {
        const schema = schemaMap[f.fieldName];
        if (!schema) return f;
        return {
          ...f,
          label: schema.label || f.label,
          width: schema.defaultWidth || f.width
        };
      }));
      setHasChanges(true);
      notify.success('Fields synced from schema');
    } catch {
      notify.error('Failed to sync fields');
    }
  };

  // ─── Sync canvas → views when switching tabs ───────────────────────────────
  const syncCanvasToView = useCallback((viewIdx) => {
    setViews(prev => {
      const updated = [...prev];
      if (updated[viewIdx]) {
        updated[viewIdx] = {
          ...updated[viewIdx],
          fieldConfig: canvasFields,
          listPanelConfig: lvHeaderSettings
        };
      }
      return updated;
    });
  }, [canvasFields, lvHeaderSettings]);

  const switchView = (newIdx) => {
    syncCanvasToView(activeViewIndex);
    setActiveViewIndex(newIdx);
    const newView = views[newIdx];
    setCanvasFields(newView?.fieldConfig || []);
    setLvHeaderSettings(newView?.listPanelConfig || { primaryLine: '', secondaryLine: '', sortField: '', sortDirection: 'ASC' });
    setPaletteSearch('');
    loadPaletteForView(newView);
  };

  // ─── Load field palette for a view ────────────────────────────────────────
  const loadPaletteForView = useCallback(async (view) => {
    if (!view?.tableName) return;
    try {
      setPaletteLoading(true);
      const res = await dataEntryApi.getTableSchema(view.tableName);
      setPaletteFields(res.data?.fields || []);
    } catch {
      setPaletteFields([]);
    } finally {
      setPaletteLoading(false);
    }
  }, []);

  useEffect(() => {
    if (views.length > 0 && views[activeViewIndex]) {
      loadPaletteForView(views[activeViewIndex]);
    }
  }, [activeViewIndex, views, loadPaletteForView]);

  // Live preview for datasheet views
  useEffect(() => {
    const activeView = views[activeViewIndex];
    if (!activeView?.tableName || activeView.viewType !== 'datasheet') {
      setPreviewData([]);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        setLoadingPreview(true);
        const res = await dynamicApi.getAll(activeView.tableName, { limit: 5 });
        if (!cancelled) setPreviewData((res.data || []).slice(0, 5));
      } catch {
        if (!cancelled) setPreviewData([]);
      } finally {
        if (!cancelled) setLoadingPreview(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [activeViewIndex, views]);

  // ─── Add/Delete View ───────────────────────────────────────────────────────
  const handleAddView = () => {
    if (!addViewTable) { notify.error('Pick a table first'); return; }
    const label = addViewLabel || addViewTable;
    const newView = {
      viewId: `v${Date.now()}`,
      viewLabel: label,
      tableName: addViewTable,
      viewType: addViewType,
      displayOrder: views.length,
      fieldConfig: [],
      listPanelConfig: { primaryLine: '', secondaryLine: '', sortField: '', sortDirection: 'ASC' }
    };
    syncCanvasToView(activeViewIndex);
    const newViews = [...views, newView];
    setViews(newViews);
    const newIdx = newViews.length - 1;
    setActiveViewIndex(newIdx);
    setCanvasFields([]);
    setLvHeaderSettings({ primaryLine: '', secondaryLine: '', sortField: '', sortDirection: 'ASC' });
    setShowAddViewModal(false);
    setAddViewTable('');
    setAddViewLabel('');
    setAddViewType('datasheet');
    setHasChanges(true);
    loadPaletteForView(newView);
  };

  const handleDeleteView = (idx) => {
    if (!window.confirm(`Remove view "${views[idx]?.viewLabel}"?`)) return;
    const updated = views.filter((_, i) => i !== idx);
    setViews(updated);
    const newIdx = Math.max(Math.min(activeViewIndex, updated.length - 1), 0);
    setActiveViewIndex(newIdx);
    const newView = updated[newIdx];
    setCanvasFields(newView?.fieldConfig || []);
    setLvHeaderSettings(newView?.listPanelConfig || { primaryLine: '', secondaryLine: '', sortField: '', sortDirection: 'ASC' });
    setHasChanges(true);
  };

  // ─── Import from SVC ───────────────────────────────────────────────────────
  const handleOpenAddView = async () => {
    setAddViewMode('new');
    setAddViewTable('');
    setAddViewLabel('');
    setAddViewType('datasheet');
    setImportSourceTable('');
    setImportSourceLabel('');
    setImportSourceConfig(null);
    setImportSvcConfigs([]);
    setShowAddViewModal(true);
  };

  const handleSwitchToImport = async () => {
    setAddViewMode('import');
    if (importSvcConfigs.length > 0) return;
    try {
      setImportSvcLoading(true);
      const res = await devFilesApi.getModuleConfigs();
      const tableNames = res.data?.configs || [];
      setImportSvcConfigs(tableNames);
    } catch {
      notify.error('Failed to load SVC configs');
    } finally {
      setImportSvcLoading(false);
    }
  };

  const handleImportSourceSelect = async (tableName) => {
    setImportSourceTable(tableName);
    setImportSourceLabel(tableName ? formatLabel(tableName) : '');
    setImportSourceConfig(null);
    if (!tableName) return;
    try {
      setImportConfigLoading(true);
      const res = await devFilesApi.getModuleConfig(tableName);
      setImportSourceConfig(res.data);
    } catch {
      notify.error('Failed to load view config');
    } finally {
      setImportConfigLoading(false);
    }
  };

  const handleImportView = () => {
    if (!importSourceTable || !importSourceConfig) return;
    const sourceFields = importSourceConfig.detailPanel?.fields || [];
    const sourceListPanel = importSourceConfig.listPanel || {};
    const fieldConfig = sourceFields.map(f => ({
      ...f,
      entryMode: 'editable'
    }));
    const listPanelConfig = {
      primaryLine: sourceListPanel.primaryLine || '',
      secondaryLine: sourceListPanel.secondaryLine || '',
      sortField: sourceListPanel.sortField || '',
      sortDirection: sourceListPanel.sortDirection || 'ASC'
    };
    const label = importSourceLabel || importSourceTable;
    const newView = {
      viewId: `v${Date.now()}`,
      viewLabel: label,
      tableName: importSourceTable,
      viewType: 'list',
      displayOrder: views.length,
      fieldConfig,
      listPanelConfig
    };
    syncCanvasToView(activeViewIndex);
    const newViews = [...views, newView];
    setViews(newViews);
    const newIdx = newViews.length - 1;
    setActiveViewIndex(newIdx);
    setCanvasFields(fieldConfig);
    setLvHeaderSettings(listPanelConfig);
    setShowAddViewModal(false);
    setImportSourceTable('');
    setImportSourceLabel('');
    setImportSourceConfig(null);
    setHasChanges(true);
    loadPaletteForView(newView);
  };

  // ─── Datasheet canvas field management ────────────────────────────────────
  const isFieldOnCanvas = (fieldName) => canvasFields.some(f => f.fieldName === fieldName);

  const addFieldToCanvas = (paletteField) => {
    if (isFieldOnCanvas(paletteField.fieldName)) return;
    setCanvasFields(prev => [...prev, {
      fieldName: paletteField.fieldName,
      entryMode: paletteField.uiType === 'readonly' ? 'display-only' : 'editable',
      label: paletteField.label,
      width: paletteField.defaultWidth || 150,
      visible: true,
      alignment: 'left'
    }]);
    setHasChanges(true);
  };

  // ─── Datasheet drag-drop reorder ──────────────────────────────────────────
  const handleDragStart = (e, idx) => {
    setDraggedField(idx);
    draggedFieldRef.current = idx;
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    const fromIdx = draggedFieldRef.current;
    if (fromIdx !== null && fromIdx !== idx) {
      setCanvasFields(prev => {
        const updated = [...prev];
        const [moved] = updated.splice(fromIdx, 1);
        updated.splice(idx, 0, moved);
        return updated;
      });
      setDraggedField(idx);
      draggedFieldRef.current = idx;
      setHasChanges(true);
    }
  };

  const handleDragEnd = () => {
    setDraggedField(null);
    draggedFieldRef.current = null;
  };

  // ─── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!selectedSlug || !moduleConfig) return;
    try {
      setSaving(true);
      const finalViews = views.map((v, i) =>
        i === activeViewIndex
          ? { ...v, fieldConfig: canvasFields, listPanelConfig: lvHeaderSettings }
          : v
      );
      await dataEntryApi.saveModule(selectedSlug, {
        module_Name: moduleConfig.module_Name,
        module_Slug: moduleConfig.module_Slug,
        description: moduleConfig.description,
        icon_Name: moduleConfig.icon_Name,
        display_Order: moduleConfig.display_Order || 0,
        config: { views: finalViews }
      });
      setViews(finalViews);
      setHasChanges(false);
      notify.status('Module saved');
    } catch {
      notify.error('Failed to save module');
    } finally {
      setSaving(false);
    }
  };

  // ─── Create new module ─────────────────────────────────────────────────────
  const handleCreateModule = async () => {
    if (!newModuleName.trim()) { notify.error('Module name required'); return; }
    const slug = newModuleSlug || slugify(newModuleName);
    try {
      await dataEntryApi.saveModule(slug, {
        module_Name: newModuleName, module_Slug: slug,
        description: newModuleDesc, icon_Name: newModuleIcon,
        display_Order: modules.length, config: { views: [] }
      });
      await loadModules();
      setShowNewModuleModal(false);
      setNewModuleName(''); setNewModuleSlug(''); setNewModuleIcon(''); setNewModuleDesc('');
      handleSelectModule(slug);
    } catch {
      notify.error('Failed to create module');
    }
  };

  // ─── List View helpers ─────────────────────────────────────────────────────
  const filteredPalette = paletteFields.filter(f =>
    !paletteSearch || f.fieldName.toLowerCase().includes(paletteSearch.toLowerCase()) ||
    f.label?.toLowerCase().includes(paletteSearch.toLowerCase())
  );

  const lvGetAvailableFields = () => {
    const placedNames = canvasFields
      .filter(f => f.fieldType !== 'section' && f.fieldType !== 'spacer' && !f.fieldName?.startsWith('__'))
      .map(f => f.fieldName);
    let fields = paletteFields.filter(f => !placedNames.includes(f.fieldName));
    if (paletteSearch.trim()) {
      const term = paletteSearch.toLowerCase();
      fields = fields.filter(f => f.fieldName.toLowerCase().includes(term) || f.label?.toLowerCase().includes(term));
    }
    return fields;
  };

  const lvGetCanvasRows = () => {
    const rows = {};
    canvasFields.forEach(field => {
      const rowNum = field.row || 1;
      if (!rows[rowNum]) rows[rowNum] = [];
      rows[rowNum].push(field);
    });
    Object.keys(rows).forEach(rowNum => rows[rowNum].sort((a, b) => (a.order || 1) - (b.order || 1)));
    const maxRow = Math.max(0, ...Object.keys(rows).map(Number));
    if (!rows[maxRow + 1]) rows[maxRow + 1] = [];
    return rows;
  };

  const lvGetFieldDataType = (fieldName) => paletteFields.find(p => p.fieldName === fieldName)?.dataType || null;
  const lvGetAllFieldNames = () => paletteFields.map(f => f.fieldName);
  // ─── List View drag handlers ───────────────────────────────────────────────
  const lvHandlePaletteDragStart = (e, field) => {
    setLvDraggedField({ ...field, isNew: true });
    e.dataTransfer.setData('text/plain', field.fieldName);
    e.dataTransfer.effectAllowed = 'move';
  };

  const lvHandleCanvasDragStart = (e, field) => {
    setLvDraggedField({ ...field, isNew: false });
    e.dataTransfer.setData('text/plain', field.fieldName);
    e.dataTransfer.effectAllowed = 'move';
  };

  const lvHandleDrop = (e, targetRow) => {
    e.preventDefault();
    e.stopPropagation();
    setLvDragOverRow(null);
    if (!lvDraggedField) return;
    if (lvDraggedField.isNew) {
      const existingInRow = canvasFields.filter(f => f.row === targetRow);
      const fkTable = lvDraggedField.fkTable || null;
      const fieldType = (lvDraggedField.uiType && lvDraggedField.uiType !== 'text')
        ? lvDraggedField.uiType : fkTable ? 'dropdown' : suggestFieldType(lvDraggedField.dataType);
      setCanvasFields(prev => [...prev, {
        fieldName: lvDraggedField.fieldName,
        label: lvDraggedField.label || formatLabel(lvDraggedField.fieldName),
        row: targetRow, order: existingInRow.length + 1,
        colSpan: 8, labelPosition: 'top', labelAlignment: 'left',
        fieldType, fkTable: fkTable || null, dataType: lvDraggedField.dataType, entryMode: 'editable'
      }]);
    } else {
      setCanvasFields(prev => prev.map(f => {
        if (f.fieldName === lvDraggedField.fieldName) {
          const existingInRow = canvasFields.filter(cf => cf.row === targetRow && cf.fieldName !== f.fieldName);
          return { ...f, row: targetRow, order: existingInRow.length + 1 };
        }
        return f;
      }));
    }
    setLvDraggedField(null);
    setHasChanges(true);
  };

  const lvHandleDragEnd = () => {
    setLvDraggedField(null);
    setLvDragOverRow(null);
    setLvDragOverInsert(null);
  };

  const lvHandleDragOver = (e, rowNum) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setLvDragOverRow(rowNum);
  };

  const lvHandleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setLvDragOverRow(null);
  };

  const lvHandleDropInsert = (e, afterRow) => {
    e.preventDefault();
    e.stopPropagation();
    setLvDragOverInsert(null);
    setLvDragOverRow(null);
    if (!lvDraggedField) return;
    const insertAt = afterRow + 1;
    const shifted = canvasFields.map(f => f.row >= insertAt ? { ...f, row: f.row + 1 } : f);
    if (lvDraggedField.isNew) {
      const fkTable = lvDraggedField.fkTable || null;
      const fieldType = (lvDraggedField.uiType && lvDraggedField.uiType !== 'text')
        ? lvDraggedField.uiType : fkTable ? 'dropdown' : suggestFieldType(lvDraggedField.dataType);
      setCanvasFields([...shifted, {
        fieldName: lvDraggedField.fieldName,
        label: lvDraggedField.label || formatLabel(lvDraggedField.fieldName),
        row: insertAt, order: 1, colSpan: 8,
        labelPosition: 'top', labelAlignment: 'left',
        fieldType, fkTable: fkTable || null, dataType: lvDraggedField.dataType, entryMode: 'editable'
      }]);
    } else {
      const without = shifted.filter(f => f.fieldName !== lvDraggedField.fieldName);
      setCanvasFields([...without, { ...lvDraggedField, row: insertAt, order: 1 }]);
    }
    setLvDraggedField(null);
    setHasChanges(true);
  };

  const lvHandleRemoveField = (fieldName) => {
    setCanvasFields(prev => prev.filter(f => f.fieldName !== fieldName));
    setHasChanges(true);
  };

  const lvHandleRemoveAll = () => {
    if (canvasFields.length === 0) return;
    notify.confirm('Remove all fields from the layout?', () => { setCanvasFields([]); setHasChanges(true); });
  };

  // ─── List View sections / spacers / derived ────────────────────────────────
  const lvHandleAddSection = () => {
    if (!lvNewSectionTitle.trim()) return;
    const updatedFields = canvasFields.map(f => f.row >= lvSectionTargetRow ? { ...f, row: f.row + 1 } : f);
    setCanvasFields([...updatedFields, {
      fieldName: `__section_${Date.now()}`, label: lvNewSectionTitle,
      row: lvSectionTargetRow, order: 0, colSpan: 24,
      labelPosition: 'top', fieldType: 'section', sectionTitle: lvNewSectionTitle
    }]);
    setLvShowSectionModal(false);
    setLvNewSectionTitle('');
    setHasChanges(true);
  };

  const lvHandleMoveSection = (sectionFieldName, direction) => {
    const section = canvasFields.find(f => f.fieldName === sectionFieldName);
    if (!section) return;
    const currentRow = section.row;
    const targetRow = direction === 'up' ? currentRow - 1 : currentRow + 1;
    if (targetRow < 1) return;
    const maxRow = Math.max(...canvasFields.map(f => f.row));
    if (direction === 'down' && currentRow >= maxRow) return;
    setCanvasFields(canvasFields.map(f => {
      if (f.fieldName === sectionFieldName) return { ...f, row: targetRow };
      if (f.row === targetRow) return { ...f, row: currentRow };
      return f;
    }));
    setHasChanges(true);
  };

  const lvHandleDerivedSourceChange = async (sourceFieldName) => {
    setLvDerivedSourceField(sourceFieldName);
    setLvDerivedTargetField('');
    setLvDerivedTargetFields([]);
    if (!sourceFieldName) return;
    const fkTable = paletteFields.find(f => f.fieldName === sourceFieldName)?.fkTable;
    if (!fkTable) return;
    setLvDerivedTargetLoading(true);
    try {
      const res = await dataEntryApi.getTableSchema(fkTable);
      setLvDerivedTargetFields((res.data?.fields || []).map(f => f.fieldName));
    } catch { setLvDerivedTargetFields([]); }
    finally { setLvDerivedTargetLoading(false); }
  };

  const lvHandleAddDerivedField = () => {
    if (!lvDerivedSourceField || !lvDerivedTargetField) return;
    const nextRow = Math.max(0, ...canvasFields.map(f => f.row)) + 1;
    setCanvasFields(prev => [...prev, {
      fieldName: `${lvDerivedTargetField}_Derived`,
      label: lvDerivedLabel || lvDerivedTargetField,
      row: nextRow, order: 0, colSpan: 8,
      labelPosition: 'top', labelAlignment: 'left',
      fieldType: 'fk-derived',
      sourceField: lvDerivedSourceField, targetField: lvDerivedTargetField,
      entryMode: 'display-only'
    }]);
    setLvShowDerivedModal(false);
    setLvDerivedSourceField(''); setLvDerivedTargetField(''); setLvDerivedLabel('');
    setHasChanges(true);
  };

  const lvHandleEditField = (field) => {
    setLvEditingField({ ...field, labelPosition: field.labelPosition || 'top', labelAlignment: field.labelAlignment || 'left' });
  };

  const lvHandleSaveFieldProperties = () => {
    if (!lvEditingField) return;
    setCanvasFields(prev => prev.map(f => f.fieldName === lvEditingField.fieldName ? lvEditingField : f));
    setLvEditingField(null);
    setHasChanges(true);
  };

  // ─── Derived state ─────────────────────────────────────────────────────────
  const selectedModuleName = modules.find(m => m.module_Slug === selectedSlug)?.module_Name || 'Select Module';

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="module-container standard-view-config-module">

      {/* ── Header bar ─────────────────────────────────────────────────────── */}
      <div className="lvc-header">
        <div className="lvc-header-left">
          <h2>Data Entry Composer</h2>
          <div className="lvc-table-dropdown-wrapper">
            <label className="lvc-dropdown-label">Module</label>
            <div className="lvc-custom-dropdown" ref={moduleDropdownRef}>
              <button
                type="button"
                className="lvc-dropdown-trigger"
                onClick={() => setShowModuleDropdown(prev => !prev)}
              >
                {hasChanges && <span className="unsaved-dot" title="Unsaved changes" style={{ marginRight: 4 }} />}
                <span>{selectedSlug ? selectedModuleName : '-- Select --'}</span>
                <ChevronDown size={16} />
              </button>
              {showModuleDropdown && (
                <div className="lvc-dropdown-panel">
                  <div className="lvc-dropdown-options">
                    {modules.map(m => (
                      <div
                        key={m.module_Slug}
                        className={`lvc-dropdown-option${selectedSlug === m.module_Slug ? ' selected' : ''}`}
                        onClick={() => handleSelectModule(m.module_Slug)}
                      >
                        <span className="lvc-option-name">{m.module_Name}</span>
                        {m.hasConfig && <span className="lvc-option-icons" title="Has views configured">●</span>}
                      </div>
                    ))}
                    <div style={{ borderTop: '1px solid var(--color-gray-200)', margin: '4px 0' }} />
                    <div className="lvc-dropdown-option" onClick={() => { setShowModuleDropdown(false); setShowNewModuleModal(true); }}>
                      <Plus size={13} style={{ marginRight: 4 }} />New Module...
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          {selectedSlug && activeView?.tableName && (
            <>
              <button
                className="crud-btn"
                onClick={handleRefreshSchema}
                disabled={refreshingSchema}
                title="Refresh schema for active view's table"
              >
                {refreshingSchema ? <RefreshCw size={18} className="spinning" /> : <Database size={18} />}
              </button>
              <button
                className="crud-btn"
                onClick={handleReloadConfig}
                disabled={loadingModule}
                title="Reload saved config (discard unsaved changes)"
              >
                <FileDown size={18} />
              </button>
              <button
                className="crud-btn"
                onClick={handleSyncFields}
                disabled={canvasFields.length === 0}
                title="Sync field labels and widths from schema"
              >
                <DatabaseZap size={18} />
              </button>
            </>
          )}
        </div>
        <div className="lvc-header-right">
          {hasChanges && <span className="unsaved-indicator">Unsaved</span>}
          <button
            className={`crud-btn${!selectedSlug || !hasChanges ? ' disabled' : ''}`}
            onClick={handleSave}
            disabled={saving || !selectedSlug || !hasChanges}
            title="Save module config"
          >
            <Save size={18} />
          </button>
        </div>
      </div>

      {/* ── No module selected ─────────────────────────────────────────────── */}
      {!selectedSlug && (
        <div className="lvc-empty-state">
          <h3>Select a Module</h3>
          <p>Choose a module from the dropdown to start configuring its views and fields.</p>
        </div>
      )}

      {/* ── Loading ────────────────────────────────────────────────────────── */}
      {selectedSlug && loadingModule && <div className="loading">Loading module...</div>}

      {/* ── View tab strip ─────────────────────────────────────────────────── */}
      {selectedSlug && !loadingModule && (
        <div className="lvc-config-tabs">
          {views.map((view, idx) => (
            <button
              key={view.viewId || idx}
              className={`lvc-config-tab${idx === activeViewIndex ? ' active' : ''}`}
              onClick={() => idx !== activeViewIndex && switchView(idx)}
            >
              {view.viewType === 'datasheet' ? <Table size={14} /> : <LayoutList size={14} />}
              <span>{view.viewLabel}</span>
              <span
                style={{ marginLeft: 4, opacity: 0.6, fontSize: 13, cursor: 'pointer' }}
                onClick={(e) => { e.stopPropagation(); handleDeleteView(idx); }}
                title="Remove view"
              >×</span>
            </button>
          ))}
          <button
            className="lvc-config-tab"
            onClick={handleOpenAddView}
            title="Add a new view"
            style={{ border: '1px dashed var(--color-gray-400)', color: 'var(--color-gray-500)' }}
          >
            <Plus size={14} /> Add View
          </button>
        </div>
      )}

      {/* ── Main content ───────────────────────────────────────────────────── */}
      {selectedSlug && !loadingModule && (
        <>
          {/* ════ DATASHEET VIEW ════ */}
          {activeView?.viewType === 'datasheet' && (
            <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>

              {/* Left: palette */}
              <div className="list-panel lvc-fields-panel">
                <div className="list-panel-header">
                  {paletteLoading && <RefreshCw size={13} className="spinning" />}
                  <span className="panel-title">{activeView ? `${activeView.tableName} Fields` : 'Fields'}</span>
                </div>
                <div className="lvc-search">
                  <Search size={13} />
                  <input type="text" placeholder="Search fields..." value={paletteSearch} onChange={e => setPaletteSearch(e.target.value)} />
                </div>
                <div className="list-scroll" style={{ flex: 1, overflowY: 'auto', padding: '6px 8px' }}>
                  {filteredPalette.map(f => {
                    const onCanvas = isFieldOnCanvas(f.fieldName);
                    return (
                      <div
                        key={f.fieldName}
                        className={`lvc-field-item${onCanvas ? ' disabled' : ''}`}
                        onClick={() => !onCanvas && addFieldToCanvas(f)}
                        style={{ cursor: onCanvas ? 'default' : 'pointer', opacity: onCanvas ? 0.45 : 1, marginBottom: 3 }}
                        title={onCanvas ? 'Already on canvas' : `Add ${f.fieldName}`}
                      >
                        <span className="drag-handle">{getFieldIcon(f.uiType)}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <span className="field-name">{f.fieldName}</span>
                          {f.label !== f.fieldName && (
                            <span className="lvc-meta" style={{ display: 'block', fontSize: 10, color: 'var(--color-gray-400)' }}>{f.label}</span>
                          )}
                        </div>
                        {onCanvas && <span style={{ color: 'var(--color-green-600)', fontSize: 11 }}>✓</span>}
                      </div>
                    );
                  })}
                  {filteredPalette.length === 0 && !paletteLoading && (
                    <div className="lvc-empty">{paletteSearch ? 'No matching fields' : activeView ? 'No fields found' : 'Select a view'}</div>
                  )}
                </div>
              </div>

              {/* Right: datasheet canvas */}
              <div className="detail-panel lvc-canvas-panel">
                <div className="lvc-datasheet-config">
                  <div className="lvc-full-section-header">
                    <span>Datasheet Columns</span>
                    <span className="lvc-related-count">({canvasFields.filter(f => f.visible !== false).length} visible)</span>
                  </div>
                  <div className="lvc-datasheet-list">
                    <div className="lvc-ds-header" style={{ gridTemplateColumns: '32px 50px 1fr 1fr 80px 110px 120px' }}>
                      <span className="ds-col-drag" /><span className="ds-col-visible">Show</span>
                      <span className="ds-col-field">Field</span><span className="ds-col-label">Label</span>
                      <span className="ds-col-width">Width</span><span className="ds-col-align">Align</span>
                      <span>Entry Mode</span>
                    </div>
                    {canvasFields.map((field, idx) => (
                      <div
                        key={field.fieldName}
                        className={`lvc-ds-row${draggedField === idx ? ' dragging' : ''}`}
                        style={{ gridTemplateColumns: '32px 50px 1fr 1fr 80px 110px 120px' }}
                        draggable
                        onDragStart={e => handleDragStart(e, idx)}
                        onDragOver={e => handleDragOver(e, idx)}
                        onDragEnd={handleDragEnd}
                      >
                        <span className="ds-col-drag"><GripVertical size={14} /></span>
                        <span className="ds-col-visible">
                          <input type="checkbox" checked={field.visible !== false} onChange={e => {
                            setCanvasFields(prev => { const u = [...prev]; u[idx] = { ...u[idx], visible: e.target.checked }; return u; });
                            setHasChanges(true);
                          }} />
                        </span>
                        <span className="ds-col-field" title={field.fieldName}>{field.fieldName}</span>
                        <span className="ds-col-label">
                          <input type="text" className="ds-input" value={field.label || ''} onChange={e => {
                            setCanvasFields(prev => { const u = [...prev]; u[idx] = { ...u[idx], label: e.target.value }; return u; });
                            setHasChanges(true);
                          }} />
                        </span>
                        <span className="ds-col-width">
                          <input type="number" step={20} className="ds-input ds-input-sm" min={50} max={500} value={field.width || 150} onChange={e => {
                            setCanvasFields(prev => { const u = [...prev]; u[idx] = { ...u[idx], width: parseInt(e.target.value) || 150 }; return u; });
                            setHasChanges(true);
                          }} />
                        </span>
                        <span className="ds-col-align">
                          {['left', 'center', 'right'].map(a => (
                            <button key={a} className={`ds-align-btn${(field.alignment || 'left') === a ? ' active' : ''}`}
                              onClick={() => { setCanvasFields(prev => { const u = [...prev]; u[idx] = { ...u[idx], alignment: a }; return u; }); setHasChanges(true); }}
                              title={a.charAt(0).toUpperCase() + a.slice(1)}>
                              {a === 'left' ? <AlignLeft size={14} /> : a === 'center' ? <AlignCenter size={14} /> : <AlignRight size={14} />}
                            </button>
                          ))}
                        </span>
                        <span>
                          <select className="ds-input" value={field.entryMode || 'editable'} onChange={e => {
                            setCanvasFields(prev => { const u = [...prev]; u[idx] = { ...u[idx], entryMode: e.target.value }; return u; });
                            setHasChanges(true);
                          }} style={{ fontSize: 'var(--text-xs)', padding: '3px 4px' }}>
                            <option value="editable">Editable</option>
                            <option value="display-only">Display Only</option>
                            <option value="hidden">Hidden</option>
                          </select>
                        </span>
                      </div>
                    ))}
                    {canvasFields.length === 0 && <div className="lvc-ds-empty">Click fields in the palette to add them here.</div>}
                  </div>

                  {/* Live Preview */}
                  {canvasFields.length > 0 && (
                    <>
                      <div className="lvc-full-section-header" style={{ marginTop: 'var(--spacing-md)' }}>
                        <span>Live Preview</span>
                        {loadingPreview && <span className="lvc-preview-note">Loading...</span>}
                        {!loadingPreview && previewData.length === 0 && <span className="lvc-preview-note">(No API — headers only)</span>}
                      </div>
                      <div className="lvc-ds-preview-wrapper">
                        <div className="lvc-ds-preview">
                          <table className="lvc-ds-preview-table" style={{ tableLayout: 'fixed', width: canvasFields.filter(f => f.visible !== false).reduce((sum, f) => sum + (f.width || 150), 0) + 'px' }}>
                            <colgroup>
                              {canvasFields.filter(f => f.visible !== false).map(f => <col key={f.fieldName} style={{ width: `${f.width || 150}px` }} />)}
                            </colgroup>
                            <thead>
                              <tr>
                                {canvasFields.filter(f => f.visible !== false).map(f => (
                                  <th key={f.fieldName} style={{ textAlign: f.alignment || 'left' }}><span>{f.label || f.fieldName}</span></th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {previewData.length > 0 ? previewData.map((row, rowIdx) => (
                                <tr key={rowIdx}>
                                  {canvasFields.filter(f => f.visible !== false).map(f => {
                                    const key = f.fieldName.charAt(0).toLowerCase() + f.fieldName.slice(1);
                                    const val = row[key];
                                    return <td key={f.fieldName} style={{ textAlign: f.alignment || 'left' }}>{typeof val === 'boolean' ? (val ? '✓' : '') : (val ?? '')}</td>;
                                  })}
                                </tr>
                              )) : (
                                <tr className="lvc-preview-empty-row">
                                  <td colSpan={canvasFields.filter(f => f.visible !== false).length}>Connect API to see sample data</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ════ LIST VIEW ════ */}
          {activeView?.viewType === 'list' && (
            <>
              {/* Toolbar */}
              <div className="lvc-full-section-header">
                <span>Detail Panel Layout</span>
                <div className="lvc-section-header-controls">
                  <button className="lvc-section-btn" onClick={() => { setLvSectionTargetRow(Math.max(1, ...canvasFields.map(f => f.row), 0) + 1); setLvShowSectionModal(true); }} title="Insert section divider">
                    <SplitSquareHorizontal size={16} style={{ transform: 'rotate(90deg)' }} />
                  </button>
                  <button className="lvc-section-btn" onClick={() => setLvShowDerivedModal(true)} title="Add derived field"><Plus size={16} /></button>
                  <button className="lvc-section-btn" onClick={() => {
                    const nextRow = Math.max(0, ...canvasFields.map(f => f.row)) + 1;
                    setCanvasFields(prev => [...prev, { fieldName: `__spacer_${Date.now()}`, label: 'Spacer', row: nextRow, order: 0, colSpan: 4, labelPosition: 'top', labelAlignment: 'left', fieldType: 'spacer' }]);
                    setHasChanges(true);
                  }} title="Add spacer block">
                    <AlignCenter size={16} />
                  </button>
                  <button className="lvc-section-btn danger" onClick={lvHandleRemoveAll} disabled={canvasFields.length === 0} title="Remove all fields">
                    <ChevronsLeft size={16} />
                  </button>
                </div>
              </div>

              {/* Two-pane: palette + form canvas */}
              <div className={`list-view ${lvListPanelCollapsed ? 'list-collapsed' : ''}`}>

                {/* Left: Available Fields */}
                {lvListPanelCollapsed ? (
                  <div className="list-panel-collapsed">
                    <button onClick={() => setLvListPanelCollapsed(false)} className="list-panel-expand-btn" title="Expand"><ChevronRight size={16} /></button>
                  </div>
                ) : (
                  <div
                    className={`list-panel lvc-fields-panel ${lvDragOverRow === 'palette' ? 'drag-over' : ''}`}
                    onDragOver={(e) => { e.preventDefault(); setLvDragOverRow('palette'); }}
                    onDragLeave={() => setLvDragOverRow(null)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setLvDragOverRow(null);
                      if (lvDraggedField && !lvDraggedField.isNew) { lvHandleRemoveField(lvDraggedField.fieldName); setLvDraggedField(null); }
                    }}
                  >
                    <div className="list-panel-header">
                      <button className="lvc-section-btn" onClick={() => setLvShowListConfigModal(true)} title="List View Search Configuration"><ListFilter size={16} /></button>
                      <span className="panel-title">Available Fields ({lvGetAvailableFields().length})</span>
                      <button onClick={() => setLvListPanelCollapsed(true)} className="list-panel-collapse-btn" title="Collapse"><ChevronLeft size={16} /></button>
                    </div>
                    <div className="lvc-panel-meta">
                      {canvasFields.filter(f => f.fieldType !== 'section' && f.fieldType !== 'spacer' && !f.fieldName?.startsWith('__')).length} / {paletteFields.length} placed
                    </div>
                    <div className="lvc-search">
                      <Search size={14} />
                      <input type="text" placeholder="Search..." value={paletteSearch} onChange={e => setPaletteSearch(e.target.value)} />
                      {paletteSearch && <button onClick={() => setPaletteSearch('')}><X size={14} /></button>}
                    </div>
                    <div className="list-scroll">
                      {lvGetAvailableFields().map(field => (
                        <div
                          key={field.fieldName}
                          className="lvc-field-item"
                          draggable
                          onDragStart={(e) => lvHandlePaletteDragStart(e, field)}
                          onDragEnd={lvHandleDragEnd}
                        >
                          <GripVertical size={12} className="drag-handle" />
                          <span className="field-name">{field.fieldName}</span>
                          <div className="field-badges">
                            {field.fkTable && <span className="fk-badge" title={`FK → ${field.fkTable}`}><Link2 size={9} /> {field.fkTable}</span>}
                            <span className={`type-badge ${getTypeColor(field.dataType)}`}>{getShortType(field.dataType)}</span>
                          </div>
                        </div>
                      ))}
                      {lvGetAvailableFields().length === 0 && !paletteLoading && (
                        <div className="lvc-empty">{paletteSearch ? 'No matches' : 'All fields placed'}</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Right: Form canvas */}
                <div className="detail-panel lvc-canvas-panel">
                  <div className="lvc-form-canvas">
                    {Object.entries(lvGetCanvasRows()).sort((a, b) => Number(a[0]) - Number(b[0])).map(([rowNum, fields], idx) => (
                      <React.Fragment key={rowNum}>
                        {idx === 0 && (
                          <div
                            className={`lvc-insert-strip ${lvDragOverInsert === 0 ? 'active' : ''}`}
                            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setLvDragOverInsert(0); setLvDragOverRow(null); }}
                            onDragLeave={() => setLvDragOverInsert(null)}
                            onDrop={(e) => lvHandleDropInsert(e, 0)}
                          />
                        )}
                        <div
                          className={`lvc-canvas-row ${lvDragOverRow === Number(rowNum) ? 'drag-over' : ''}`}
                          onDragOver={(e) => lvHandleDragOver(e, Number(rowNum))}
                          onDragLeave={lvHandleDragLeave}
                          onDrop={(e) => lvHandleDrop(e, Number(rowNum))}
                        >
                          <span className="row-label">R{rowNum}</span>
                          <div className="detail-grid-24" onDragOver={(e) => lvHandleDragOver(e, Number(rowNum))} onDrop={(e) => lvHandleDrop(e, Number(rowNum))}>
                            {fields.length === 0 ? (
                              <div className="col-span-24 lvc-drop-hint" onDragOver={(e) => lvHandleDragOver(e, Number(rowNum))} onDrop={(e) => lvHandleDrop(e, Number(rowNum))}>Drop fields here</div>
                            ) : fields.map(field => {
                              const dataType = field.dataType || lvGetFieldDataType(field.fieldName);
                              if (field.fieldType === 'section') {
                                const maxRow = Math.max(...canvasFields.map(f => f.row));
                                return (
                                  <div key={field.fieldName} className="col-span-24">
                                    <div className="detail-section-header lvc-section">
                                      <span className="section-title">{field.sectionTitle}</span>
                                      <div className="section-controls">
                                        <button className="lvc-section-btn" onClick={() => lvHandleMoveSection(field.fieldName, 'up')} disabled={field.row <= 1} title="Move Up"><ChevronUp size={14} /></button>
                                        <button className="lvc-section-btn" onClick={() => lvHandleMoveSection(field.fieldName, 'down')} disabled={field.row >= maxRow} title="Move Down"><ChevronDown size={14} /></button>
                                        <button className="lvc-section-btn delete" onClick={() => lvHandleRemoveField(field.fieldName)} title="Remove"><X size={14} /></button>
                                      </div>
                                    </div>
                                  </div>
                                );
                              }
                              return (
                                <div
                                  key={field.fieldName}
                                  className={`col-span-${field.colSpan} lvc-placed-field label-${field.labelPosition || 'top'}`}
                                  draggable
                                  onDragStart={(e) => lvHandleCanvasDragStart(e, field)}
                                  onDragEnd={lvHandleDragEnd}
                                  onDoubleClick={() => lvHandleEditField(field)}
                                >
                                  <div className="lvc-tile-label">
                                    <GripVertical size={10} className="drag-handle" />
                                    <span className="label-text">{field.label}</span>
                                  </div>
                                  <div className="lvc-tile-input">
                                    <div className="input-icons">
                                      {field.fkTable && <span className="fk-badge" title={`FK → ${field.fkTable}`}><Link2 size={9} /> {field.fkTable}</span>}
                                      <span className={`field-type-badge ${field.fieldType || 'text'}`} title={field.fieldType || 'text'}>{getFieldTypeIcon(field.fieldType)}</span>
                                      <span className={`type-badge ${getTypeColor(dataType)}`}>{getShortType(dataType)}</span>
                                    </div>
                                    <span className="colspan-indicator">{field.colSpan}</span>
                                  </div>
                                  <div className="lvc-tile-controls">
                                    <button onClick={(e) => { e.stopPropagation(); lvHandleEditField(field); }} title="Edit"><Settings2 size={12} /></button>
                                    <button onClick={(e) => { e.stopPropagation(); lvHandleRemoveField(field.fieldName); }} title="Remove"><X size={12} /></button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        <div
                          className={`lvc-insert-strip ${lvDragOverInsert === Number(rowNum) ? 'active' : ''}`}
                          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setLvDragOverInsert(Number(rowNum)); setLvDragOverRow(null); }}
                          onDragLeave={() => setLvDragOverInsert(null)}
                          onDrop={(e) => lvHandleDropInsert(e, Number(rowNum))}
                        />
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>

            </>
          )}

          {/* ── No active view ─────────────────────────────────────────────── */}
          {!activeView && (
            <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
              <div className="detail-panel lvc-canvas-panel">
                <div className="lvc-empty-state">
                  <h3>No Views Yet</h3>
                  <p>Click <strong>Add View</strong> above to add a List or Datasheet view to this module.</p>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* MODALS                                                                */}
      {/* ══════════════════════════════════════════════════════════════════════ */}

      {/* Add View */}
      {showAddViewModal && (
        <div className="modal-overlay" onClick={() => setShowAddViewModal(false)}>
          <div className="modal-content modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add View</h3>
              <button className="modal-close" onClick={() => setShowAddViewModal(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">

              {/* Mode toggle */}
              <div className="form-group" style={{ marginBottom: '1.25rem' }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className={`lvc-config-tab${addViewMode === 'new' ? ' active' : ''}`} onClick={() => setAddViewMode('new')} type="button"><Plus size={15} /><span>New View</span></button>
                  <button className={`lvc-config-tab${addViewMode === 'import' ? ' active' : ''}`} onClick={handleSwitchToImport} type="button"><DatabaseZap size={15} /><span>Import from SVC</span></button>
                </div>
              </div>

              {addViewMode === 'new' && (
                <>
                  <div className="form-group" style={{ marginBottom: '1.25rem' }}>
                    <label>View Type</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className={`lvc-config-tab${addViewType === 'list' ? ' active' : ''}`} onClick={() => setAddViewType('list')} type="button"><LayoutList size={16} /><span>List View</span></button>
                      <button className={`lvc-config-tab${addViewType === 'datasheet' ? ' active' : ''}`} onClick={() => setAddViewType('datasheet')} type="button"><Table size={16} /><span>Datasheet View</span></button>
                    </div>
                  </div>
                  <div className="form-group" style={{ marginBottom: '1.25rem' }}>
                    <label>Table</label>
                    <select className="form-input" value={addViewTable} onChange={e => { setAddViewTable(e.target.value); if (!addViewLabel) setAddViewLabel(e.target.value); }}>
                      <option value="">-- Select Table --</option>
                      {allTables.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Tab Label</label>
                    <input className="form-input" type="text" value={addViewLabel} onChange={e => setAddViewLabel(e.target.value)} placeholder={addViewTable || 'Tab label'} />
                  </div>
                </>
              )}

              {addViewMode === 'import' && (
                <>
                  <div className="form-group" style={{ marginBottom: '1.25rem' }}>
                    <label>Source View</label>
                    {importSvcLoading ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--color-gray-500)', fontSize: 'var(--text-sm)' }}>
                        <RefreshCw size={13} className="spinning" /> Loading configs...
                      </div>
                    ) : (
                      <select className="form-input" value={importSourceTable} onChange={e => handleImportSourceSelect(e.target.value)}>
                        <option value="">-- Select a View --</option>
                        {importSvcConfigs.map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    )}
                  </div>
                  {importSourceTable && (
                    <>
                      {importConfigLoading ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--color-gray-500)', fontSize: 'var(--text-sm)', marginBottom: '1.25rem' }}>
                          <RefreshCw size={13} className="spinning" /> Loading...
                        </div>
                      ) : importSourceConfig && (
                        <div style={{ marginBottom: '1.25rem', padding: '8px 10px', background: 'var(--color-gray-50)', borderRadius: 6, fontSize: 'var(--text-sm)', color: 'var(--color-gray-600)' }}>
                          <strong>{importSourceConfig.detailPanel?.fields?.length || 0}</strong> fields · list search config included · all fields default to <em>Editable</em>
                        </div>
                      )}
                      <div className="form-group">
                        <label>Tab Label</label>
                        <input className="form-input" type="text" value={importSourceLabel} onChange={e => setImportSourceLabel(e.target.value)} placeholder={importSourceTable} />
                      </div>
                    </>
                  )}
                </>
              )}

            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAddViewModal(false)}>Cancel</button>
              {addViewMode === 'new'
                ? <button className="btn btn-primary" onClick={handleAddView} disabled={!addViewTable}>Add View</button>
                : <button className="btn btn-primary" onClick={handleImportView} disabled={!importSourceTable || !importSourceConfig || importConfigLoading}>Import View</button>
              }
            </div>
          </div>
        </div>
      )}

      {/* New Module */}
      {showNewModuleModal && (
        <div className="modal-overlay" onClick={() => setShowNewModuleModal(false)}>
          <div className="modal-content modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>New Module</h3>
              <button className="modal-close" onClick={() => setShowNewModuleModal(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Module Name</label>
                <input type="text" value={newModuleName} onChange={e => { setNewModuleName(e.target.value); setNewModuleSlug(slugify(e.target.value)); }} placeholder="e.g. Commissioning" autoFocus />
              </div>
              <div className="form-group">
                <label>Slug</label>
                <input type="text" value={newModuleSlug} onChange={e => setNewModuleSlug(e.target.value)} placeholder="e.g. commissioning" />
                <span className="field-hint">Must match the nav destination</span>
              </div>
              <div className="form-group">
                <label>Description</label>
                <input type="text" value={newModuleDesc} onChange={e => setNewModuleDesc(e.target.value)} placeholder="Optional description" />
              </div>
              <div className="form-group">
                <label>Icon Name</label>
                <input type="text" value={newModuleIcon} onChange={e => setNewModuleIcon(e.target.value)} placeholder="e.g. Zap, ClipboardCheck" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowNewModuleModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleCreateModule} disabled={!newModuleName.trim()}>Create</button>
            </div>
          </div>
        </div>
      )}

      {/* LV: Section */}
      {lvShowSectionModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-sm">
            <div className="modal-header">
              <h3>Add Section</h3>
              <button onClick={() => setLvShowSectionModal(false)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Section Title</label>
                <input type="text" className="form-input" value={lvNewSectionTitle} onChange={e => setLvNewSectionTitle(e.target.value)} placeholder="e.g. General Information" autoFocus />
              </div>
              <div className="form-group">
                <label>Insert at Row</label>
                <input type="number" className="form-input" min="1" value={lvSectionTargetRow} onChange={e => setLvSectionTargetRow(parseInt(e.target.value) || 1)} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setLvShowSectionModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={lvHandleAddSection} disabled={!lvNewSectionTitle.trim()}>Add</button>
            </div>
          </div>
        </div>
      )}

      {/* LV: Derived field */}
      {lvShowDerivedModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-sm">
            <div className="modal-header">
              <h3>Add Derived Field</h3>
              <button onClick={() => { setLvShowDerivedModal(false); setLvDerivedSourceField(''); setLvDerivedTargetField(''); setLvDerivedLabel(''); setLvDerivedTargetFields([]); }}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <p className="form-hint" style={{ marginBottom: 12 }}>Displays a read-only value from a related record via an FK field in this table.</p>
              <div className="form-group">
                <label>Source FK Field <span style={{ fontSize: 10, color: '#888' }}>(in this table)</span></label>
                <select className="form-input" value={lvDerivedSourceField} onChange={e => lvHandleDerivedSourceChange(e.target.value)}>
                  <option value="">-- Select FK Field --</option>
                  {paletteFields.filter(f => f.fkTable).map(f => <option key={f.fieldName} value={f.fieldName}>{f.fieldName} → {f.fkTable}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Target Field <span style={{ fontSize: 10, color: '#888' }}>(on related table)</span></label>
                <select className="form-input" value={lvDerivedTargetField} onChange={e => setLvDerivedTargetField(e.target.value)} disabled={!lvDerivedSourceField || lvDerivedTargetLoading}>
                  <option value="">{lvDerivedTargetLoading ? 'Loading...' : lvDerivedSourceField ? '-- Select Field --' : '-- Select source first --'}</option>
                  {lvDerivedTargetFields.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Label</label>
                <input type="text" className="form-input" value={lvDerivedLabel} onChange={e => setLvDerivedLabel(e.target.value)} placeholder="e.g. Manufacturer" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setLvShowDerivedModal(false); setLvDerivedSourceField(''); setLvDerivedTargetField(''); setLvDerivedLabel(''); setLvDerivedTargetFields([]); }}>Cancel</button>
              <button className="btn btn-primary" onClick={lvHandleAddDerivedField} disabled={!lvDerivedSourceField || !lvDerivedTargetField}>Add</button>
            </div>
          </div>
        </div>
      )}

      {/* LV: List search config */}
      {lvShowListConfigModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-sm">
            <div className="modal-header">
              <h3>List View Search Configuration</h3>
              <button onClick={() => setLvShowListConfigModal(false)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <p className="modal-description">Configure how records appear in the list panel search results.</p>
              <div className="form-group">
                <label>Primary Line</label>
                <select className="form-input" value={lvHeaderSettings.primaryLine} onChange={e => { setLvHeaderSettings(prev => ({ ...prev, primaryLine: e.target.value })); setHasChanges(true); }}>
                  <option value="">-- Select Field --</option>
                  {lvGetAllFieldNames().map(f => <option key={f} value={f}>{f}</option>)}
                </select>
                <span className="form-hint">Main text shown for each record</span>
              </div>
              <div className="form-group">
                <label>Secondary Line</label>
                <select className="form-input" value={lvHeaderSettings.secondaryLine} onChange={e => { setLvHeaderSettings(prev => ({ ...prev, secondaryLine: e.target.value })); setHasChanges(true); }}>
                  <option value="">-- Select Field --</option>
                  {lvGetAllFieldNames().map(f => <option key={f} value={f}>{f}</option>)}
                </select>
                <span className="form-hint">Additional info shown below primary</span>
              </div>
              <div className="form-row-2col">
                <div className="form-group">
                  <label>Sort Field</label>
                  <select className="form-input" value={lvHeaderSettings.sortField} onChange={e => { setLvHeaderSettings(prev => ({ ...prev, sortField: e.target.value })); setHasChanges(true); }}>
                    <option value="">-- Select Field --</option>
                    {lvGetAllFieldNames().map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Sort Direction</label>
                  <select className="form-input" value={lvHeaderSettings.sortDirection} onChange={e => { setLvHeaderSettings(prev => ({ ...prev, sortDirection: e.target.value })); setHasChanges(true); }}>
                    <option value="ASC">Ascending</option>
                    <option value="DESC">Descending</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => setLvShowListConfigModal(false)}>Done</button>
            </div>
          </div>
        </div>
      )}

      {/* LV: Field properties */}
      {lvEditingField && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Edit: {lvEditingField.fieldName}</h3>
              <button onClick={() => setLvEditingField(null)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              {lvEditingField.fieldType !== 'spacer' && (
                <div className="form-group">
                  <label>Label</label>
                  <input type="text" className="form-input" value={lvEditingField.label} onChange={e => setLvEditingField(prev => ({ ...prev, label: e.target.value }))} />
                </div>
              )}
              <div className="form-row-2col">
                <div className="form-group">
                  <label>Col Span (1-24)</label>
                  <input type="number" className="form-input" min="1" max="24" value={lvEditingField.colSpan} onChange={e => setLvEditingField(prev => ({ ...prev, colSpan: parseInt(e.target.value) || 8 }))} />
                </div>
                {lvEditingField.fieldType !== 'spacer' && (
                  <>
                    <div className="form-group">
                      <label>Label Position</label>
                      <select className="form-input" value={lvEditingField.labelPosition} onChange={e => setLvEditingField(prev => ({ ...prev, labelPosition: e.target.value }))}>
                        <option value="top">Top</option>
                        <option value="left">Left</option>
                        <option value="right">Right</option>
                        <option value="bottom">Bottom</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Label Alignment</label>
                      <select className="form-input" value={lvEditingField.labelAlignment || 'left'} onChange={e => setLvEditingField(prev => ({ ...prev, labelAlignment: e.target.value }))}>
                        <option value="left">Left</option>
                        <option value="center">Center</option>
                        <option value="right">Right</option>
                      </select>
                    </div>
                  </>
                )}
              </div>
              {lvEditingField.fieldType !== 'spacer' && lvEditingField.fieldType !== 'fk-derived' && (
                <div className="form-group">
                  <label>Entry Mode</label>
                  <div className="radio-group">
                    {['editable', 'display-only', 'hidden'].map(mode => (
                      <label key={mode} className="radio-label">
                        <input type="radio" name="lvEntryMode" value={mode} checked={(lvEditingField.entryMode || 'editable') === mode} onChange={() => setLvEditingField(prev => ({ ...prev, entryMode: mode }))} />
                        {mode === 'editable' ? 'Editable' : mode === 'display-only' ? 'Display Only' : 'Hidden'}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setLvEditingField(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={lvHandleSaveFieldProperties}>Save</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default DataEntryComposerPage;
